/* GENERATED FILE — do not edit.
   Source: shared-ui/ui/PlayerCard.js
   Run "node shared-ui/sync.js" after editing the source. */
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import Avatar from './Avatar'

/*
 * Hover preview for a player.
 *
 * Scouts scan ranked lists and need to know "is this one worth opening?" without
 * losing their place. The card answers that: identity, physical profile, and the
 * five skill ratings the platform actually stores.
 *
 * Two requests per player, both cached for the session so a second hover is instant:
 *   manager/dashboard/userprofile/{uuid}  -> height, weight, foot, team, country, age
 *   manager/dashboard/userstats/{uuid}    -> [{StatName, Value}] skill ratings
 */

const profileCache = new Map()
const statsCache = new Map()

const OPEN_DELAY = 260   // don't fire a request for a cursor passing through
const CLOSE_DELAY = 140  // let the pointer travel from row to card

const auth = () => ({
  headers: { Authorization: 'Bearer ' + sessionStorage.getItem('mngapiToken') },
})

const first = (payload) => {
  if (Array.isArray(payload)) return payload[0]
  if (payload && Array.isArray(payload.results)) return payload.results[0]
  if (payload && payload.results) return payload.results
  return payload
}

const rows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.results)) return payload.results
  return []
}

/* "1996-04-02" / "1996-04-02T00:00:00.000Z" -> 30 */
const ageFrom = (value) => {
  if (!value) return null
  const born = new Date(value)
  if (isNaN(born.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - born.getFullYear()
  const monthDiff = now.getMonth() - born.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) age -= 1
  return age > 0 && age < 120 ? age : null
}

/* Height is stored in centimetres. */
const heightText = (value) => {
  const cm = Number(value)
  if (!Number.isFinite(cm) || cm <= 0) return null
  return cm >= 100 ? (cm / 100).toFixed(2) + ' m' : cm + ' cm'
}

const weightText = (value) => {
  const kg = Number(value)
  return Number.isFinite(kg) && kg > 0 ? kg + ' kg' : null
}

const footText = (value) => {
  const n = Number(value)
  if (n === 1) return 'Right'
  if (n === 2) return 'Left'
  return null
}

export default function PlayerCard({ uuid, name, position, points, children }) {
  const [open, setOpen] = useState(false)
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState(null)
  const [failed, setFailed] = useState(false)
  const [pos, setPos] = useState(null)

  const holdRef = useRef(null)
  const openTimer = useRef(null)
  const closeTimer = useRef(null)
  const aliveRef = useRef(true)

  useEffect(() => () => {
    aliveRef.current = false
    clearTimeout(openTimer.current)
    clearTimeout(closeTimer.current)
  }, [])

  const load = () => {
    if (!uuid) return

    if (profileCache.has(uuid)) setProfile(profileCache.get(uuid))
    if (statsCache.has(uuid)) setStats(statsCache.get(uuid))
    if (profileCache.has(uuid) && statsCache.has(uuid)) return

    const base = process.env.REACT_APP_API_SERVER

    if (!profileCache.has(uuid)) {
      axios.get(base + 'manager/dashboard/userprofile/' + uuid, auth())
        .then((res) => {
          const record = first(res.data) || {}
          profileCache.set(uuid, record)
          if (aliveRef.current) setProfile(record)
        })
        .catch(() => { if (aliveRef.current) setFailed(true) })
    }

    if (!statsCache.has(uuid)) {
      axios.get(base + 'manager/dashboard/userstats/' + uuid, auth())
        .then((res) => {
          const list = rows(res.data)
          statsCache.set(uuid, list)
          if (aliveRef.current) setStats(list)
        })
        .catch(() => { /* the card still works without ratings */ })
    }
  }

  const show = () => {
    clearTimeout(closeTimer.current)
    openTimer.current = setTimeout(() => {
      if (holdRef.current) {
        const box = holdRef.current.getBoundingClientRect()
        const W = 296
        const GAP = 12
        // Flip to the left when the row sits near the right edge of the viewport.
        const toRight = window.innerWidth - box.right >= W + GAP + 8
        const left = toRight ? box.right + GAP : box.left - GAP - W
        // Keep the card fully on screen vertically.
        const half = 150
        let top = box.top + box.height / 2
        top = Math.max(half + 8, Math.min(window.innerHeight - half - 8, top))
        setPos({ left: Math.max(8, left), top })
      }
      setOpen(true)
      load()
    }, OPEN_DELAY)
  }

  const hide = () => {
    clearTimeout(openTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY)
  }

  const meta = []
  if (profile) {
    const age = ageFrom(profile.DateOfBirthYMD || profile.DateOfBirth)
    if (age) meta.push(['Age', age])
    const h = heightText(profile.Height)
    if (h) meta.push(['Height', h])
    const w = weightText(profile.Weight)
    if (w) meta.push(['Weight', w])
    const foot = footText(profile.LeftRight)
    if (foot) meta.push(['Preferred', foot])
  }

  const skills = (stats || [])
    .map((row) => ({ name: row.StatName, value: Number(row.Value) }))
    .filter((row) => row.name && Number.isFinite(row.value))

  const team = profile && profile.Team ? String(profile.Team).trim() : ''
  const country = profile && profile.Country ? String(profile.Country).trim() : ''
  const loading = !profile && !failed

  return (
    <span
      className="pc-hold"
      ref={holdRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}

      {open && pos && createPortal((
        <span className="pc-card" role="tooltip" style={{ left: pos.left + 'px', top: pos.top + 'px' }}>
          <span className="pc-id">
            <Avatar uuid={uuid} name={name} size={42} />
            <span className="pc-head">
              <span className="pc-name">{name}</span>
              <span className="pc-sub">
                {position || 'Position not set'}
                {country ? ' · ' + country : ''}
              </span>
              {team && <span className="pc-team">{team}</span>}
            </span>
          </span>

          {loading && (
            <span className="pc-loading">
              <span className="skeleton sk-line"></span>
              <span className="skeleton sk-line short"></span>
            </span>
          )}

          {failed && <span className="pc-empty">Details unavailable right now.</span>}

          {profile && meta.length > 0 && (
            <span className="pc-meta">
              {meta.map(([label, value]) => (
                <span className="pc-meta-item" key={label}>
                  <span className="mono pc-meta-label">{label}</span>
                  <span className="pc-meta-value num">{value}</span>
                </span>
              ))}
            </span>
          )}

          {skills.length > 0 && (
            <span className="pc-skills">
              <span className="mono pc-section">Skill ratings</span>
              {skills.map((skill) => (
                <span className="pc-skill" key={skill.name}>
                  <span className="pc-skill-name">{skill.name}</span>
                  <span className="pc-skill-track">
                    <span
                      className="pc-skill-fill"
                      style={{ width: Math.max(0, Math.min(100, skill.value)) + '%' }}
                    />
                  </span>
                  <span className="pc-skill-value num">{skill.value}</span>
                </span>
              ))}
            </span>
          )}

          {points !== undefined && points !== null && (
            <span className="pc-foot">
              <span className="mono">Points</span>
              <b className="num">{points}</b>
            </span>
          )}
        </span>
      ), document.body)}
    </span>
  )
}
